import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DashboardClientScope, DashboardMetricTile, DashboardMetrics } from 'src/app/models/DashboardMetrics';
import { DashboardMetricsService } from 'src/app/services/dashboard-metrics.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { getErrorMessage } from 'src/app/utils/error-message';
import { PermissionGroups } from 'src/app/utils/PermissionGroups';

export const DASHBOARD_METRIC_GROUPS = [
  PermissionGroups.Admin,
  PermissionGroups.BillingAdmin,
  PermissionGroups.BillingContributor,
  PermissionGroups.ClientServicesAdmin,
  PermissionGroups.ClientServicesContributor
];

export const DASHBOARD_UNSCOPED_GROUPS = [
  PermissionGroups.Admin,
  PermissionGroups.BillingAdmin,
  PermissionGroups.ClientServicesAdmin
];

@UntilDestroy()
@Component({
  selector: 'vex-dashboard-metrics',
  templateUrl: './dashboard-metrics.component.html',
  styleUrls: ['./dashboard-metrics.component.scss']
})
export class DashboardMetricsComponent implements OnChanges {
  @Input() scope: DashboardClientScope = 'mine';

  loading = false;
  metrics: DashboardMetrics | null = null;

  constructor(
    private dashboardMetricsService: DashboardMetricsService,
    private snackbarService: SnackbarService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scope']) {
      this.loadMetrics();
    }
  }

  get showEmptyAssignment(): boolean {
    return !this.loading
      && this.scope === 'mine'
      && !!this.metrics
      && this.metrics.associatedClients.current === 0;
  }

  get associatedClientsLabel(): string {
    return this.scope === 'all' ? 'All Clients' : 'My Clients';
  }

  get associatedClientsTrend(): string {
    return this.formatDelta(this.metrics?.associatedClients, 'this quarter');
  }

  get activeMembersTrend(): string {
    return this.formatDelta(this.metrics?.activeMembers, 'vs last month');
  }

  get awaitingTrend(): string {
    const count = this.metrics?.enrollmentsAwaitingAcknowledgement.current ?? 0;
    return count === 0 ? 'None pending' : 'Needs follow-up';
  }

  get upcomingOpenEnrollmentTrend(): string {
    const count = this.metrics?.upcomingOpenEnrollment?.next7Days ?? 0;
    return count === 0 ? 'None in next 7 days' : `${this.formatCount(count)} in next 7 days`;
  }

  formatCount(value: number | null | undefined): string {
    return (value ?? 0).toLocaleString('en-US');
  }

  trendTone(tile: DashboardMetricTile | undefined): string {
    const delta = this.delta(tile);
    if (delta > 0) {
      return 'tone-up';
    }
    if (delta < 0) {
      return 'tone-down';
    }
    return 'tone-neutral';
  }

  private formatDelta(tile: DashboardMetricTile | undefined, suffix: string): string {
    if (tile?.previous == null) {
      return `No prior ${suffix}`;
    }
    const delta = this.delta(tile);
    if (delta === 0) {
      return `No change ${suffix}`;
    }
    const arrow = delta > 0 ? '▲' : '▼';
    return `${arrow} ${this.formatCount(Math.abs(delta))} ${suffix}`;
  }

  private delta(tile: DashboardMetricTile | undefined): number {
    if (!tile || tile.previous == null) {
      return 0;
    }
    return tile.current - tile.previous;
  }

  private loadMetrics(): void {
    this.loading = true;
    this.dashboardMetricsService.getMetrics(this.scope)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (metrics) => {
          this.metrics = metrics;
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.metrics = null;
          this.snackbarService.open(
            getErrorMessage(err, 'Failed to load dashboard metrics.'),
            'error'
          );
        }
      });
  }
}
